import { d as debounce } from './index-DfRgBxEx.js';
import { f as fetchConfig } from './index-CoP6k_Ao.js';

/**
 * Converts a string to a URL-friendly handle/slug
 * @param {string} str - The string to convert
 * @returns {string} The handleized string
 */
const handleize = (str) => {
  return str
    .toLowerCase() // Convert to lowercase
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .trim() // Remove whitespace from ends
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
};

/* Update cart items */
if (!customElements.get('cart-gift')) {
  class CartGift extends HTMLElement {
    constructor() {
      super();

      // Get the autoSelect from sessionStorage if its less than 1 hour old
      const timestamp = Date.now();
      this.autoSelect = (() => {
        let data = sessionStorage.getItem('cartGiftAutoSelect');
        if (!data) return true;
        data = JSON.parse(data);
        if (timestamp - data.timestamp <= 3600000) {
          return data.value !== 'false';
        }
        return true;
      })();
      this.autoSelectTemporary = true;

      // Get the giftIsAddedByAutoSelect from sessionStorage if its less than 1 hour old
      this.giftIsAddedByAutoSelect = (() => {
        let data = sessionStorage.getItem('cartGiftIsAddedByAutoSelect');
        if (!data) return false;
        data = JSON.parse(data);
        if (timestamp - data.timestamp <= 3600000) {
          return data.value !== 'false';
        }
        return false;
      })();

      // Get the giftId from localStorage (no expiration)
      this.giftId = localStorage.getItem('cartGiftId') || null;

      // Selectors
      this.cartItems = document.querySelector('cart-items');
      this.giftProgress = this.querySelector('.js-cart-gift-progress');
      this.giftText = this.querySelector('.js-cart-gift-progress-text');
      this.giftBar = this.querySelector('.js-cart-gift-progress-bar');
      this.giftItems = this.querySelectorAll('.js-cart-gift-item');

      // Get the options
      this.options = JSON.parse(this.dataset.options);
      if (!this.options.auto_select) {
        this.autoSelect = false;
      }

      // Listen for on change
      const debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);
      this.addEventListener('change', debouncedOnChange.bind(this));

      // Listen for cart updates in other components
      document.addEventListener('cart-updated', (event) => {
        this.updateContents();
      });

      // Listen for cart updates in other components
      document.addEventListener('quantity-updated', (event) => {
        this.updateContents();
      });
    }

    /**
     * On change
     * @param {Event} event - The event that was triggered
     */
    onChange(event) {
      // Get the target of the gift item that was changed or the first gift item
      const target = event?.target || this.giftItems[0];
      if (!target) return;

      // Get the id of the gift item that was changed
      const id = parseInt(target?.value);
      if (!id) return;

      // Is the gift active?
      const isActive = target.checked;

      // Disable / enable auto select if the gift is removed
      this.setAutoSelect(isActive);

      // Set gift
      this.setGiftIsAddedByAutoSelect(isActive);
      this.update(id, isActive);
    }

    /**
     * Update the gift item in the cart
     * Get the cart items and check if the gift item is already in the cart
     * If it is, remove the gift from the cart
     * If it is not, add the gift to the cart
     * @param {Number} id - The variant id of the gift item
     * @param {Boolean} isActive - Whether the gift item is active
     */
    async update(id, isActive = true) {
      if (!id) return;

      try {
        // Remove the checked state from the other gift items
        // And disable the checkboxes temporarily
        this.giftItems.forEach((item) => {
          item.style.pointerEvents = 'none';
          if (item.value != id) {
            item.checked = false;
          } else {
            item.checked = isActive;
          }
        });

        // Enable loading
        this.cartItems.enableLoading();

        // Get the cart
        const cartResponse = await fetch(`${routes.cart_url}`, {
          ...fetchConfig('json', 'GET'),
        });
        const cart = await cartResponse.json();

        // Get the line item that has the applied discount (the one with the property is not always the discounted item)
        let lineItem = cart.items.find((item) =>
          item.line_level_discount_allocations.some(
            (allocation) =>
              handleize(allocation.discount_application.title) ===
                handleize(this.options.discount_title) && allocation.amount > 0
          )
        );

        // If no line item is found, try to find the line item with the previous gift id for the remove action
        if (!lineItem && this.giftId && !isActive) {
          lineItem = cart.items.find((item) => item.id == this.giftId);
        }

        // Check if the line item has the same id as the event target
        const isSameProduct = lineItem && lineItem.id == id ? true : false;

        // Return if the gift is already applied
        if (isSameProduct && isActive) return;

        // Save the parsed state to update the cart items section
        let parsedState = null;

        // If the product is the same and should be removed
        if (isSameProduct && !isActive) {
          parsedState = await this.removeGift(
            lineItem.key,
            lineItem.quantity - this.options.quantity
          );
          this.setGiftId(null);
        }

        // If the product is not the same and should be added
        else if (!isSameProduct && isActive) {
          // Remove the old gift
          if (lineItem) {
            await this.removeGift(lineItem.key, lineItem.quantity - this.options.quantity);
          }

          // Add the new gift
          parsedState = await this.addGift(id);
          this.setGiftId(id);
        }

        // Update the cart items
        if (parsedState?.sections) {
          this.cartItems.renderContents(parsedState);
        }

        // Update the cart gift section
        this.updateContents(false);
      } catch (error) {
        console.error(error);
      } finally {
        // Enable the checkboxes
        this.giftItems.forEach((item) => {
          item.style.pointerEvents = 'auto';
        });

        // Disable loading
        this.cartItems.disableLoading();
      }
    }

    /**
     * @param {String} key - The key of the line item to remove
     * @param {Number} quantity - The new quantity of the line item to remove
     * Remove the gift from the cart
     */
    async removeGift(key, quantity = 0) {
      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: this.cartItems.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });

      const parsedState = await response.json();

      return parsedState;
    }

    /**
     * @param {Number} id - The id of the product to add to the cart
     * Add the gift to the cart
     */
    async addGift(id) {
      const response = await fetch(`${routes.cart_add_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [
            {
              id,
              quantity: this.options.quantity,
            },
          ],
          sections: this.cartItems.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });

      const parsedState = await response.json();

      return parsedState;
    }

    /**
     * Update the autoSelect state and sessionStorage (save for one our)
     * @param {Boolean} value - The new autoSelect value
     */
    setAutoSelect(value) {
      value = this.options.auto_select == false ? false : value;
      this.autoSelect = value;
      sessionStorage.setItem(
        'cartGiftAutoSelect',
        JSON.stringify({
          value: value.toString(),
          timestamp: Date.now(),
        })
      );
    }

    /**
     * Temporarily disable auto select
     * @param {Boolean} value - The new autoSelect value
     */
    setAutoSelectTemporary(value = true) {
      this.autoSelectTemporary = value;
    }

    /**
     * Set the giftIsAddedByAutoSelect state
     * @param {Boolean} value - The new autoSelect value
     */
    setGiftIsAddedByAutoSelect(value = false) {
      this.giftIsAddedByAutoSelect = value;
      sessionStorage.setItem(
        'cartGiftIsAddedByAutoSelect',
        JSON.stringify({
          value: value.toString(),
          timestamp: Date.now(),
        })
      );
    }

    /**
    /**
     * Update the giftId state and localStorage (no expiration)
     * @param {Number|null} value - The new giftId value
     */
    setGiftId(value) {
      this.giftId = value;
      if (value === null) {
        localStorage.removeItem('cartGiftId');
      } else {
        localStorage.setItem('cartGiftId', value.toString());
      }
    }

    /**
     * Update the cart gift section after a cart update
     * @param {Boolean} validate - Whether we need to check if we need to update the gift item in the cart
     */
    async updateContents(validate = true) {
      try {
        // Get the section ID from the cart items element
        const sectionId = document.querySelector('#main-cart-items').dataset.id;

        // Fetch the gift section from the cart page
        const response = await fetch(`${window.location.pathname}?sections=${sectionId}`);
        const parsedState = await response.json();

        // Parse the gift section
        const html = new DOMParser()
          .parseFromString(parsedState[sectionId], 'text/html')
          .querySelector('.js-cart-gift');

        // Parse the options
        const options = JSON.parse(html.dataset.options);
        const discount = options.discount;
        const previousDiscount = this.options.discount || 0;

        // Get the the checked gift item from the html
        const activeGift = html.querySelector('.js-cart-gift-item[data-in-cart="true"]');
        const activeId = activeGift?.value || null;

        // Update the gift section
        this.giftProgress.classList.toggle('!max-h-0', options.reached_threshold);
        this.giftProgress.classList.toggle('!opacity-0', options.reached_threshold);
        this.giftText.innerHTML = options.gift_progress_text;
        this.giftBar.style.width = `${options.progress}%`;
        this.giftItems.forEach((item) => {
          if (options.reached_threshold) {
            item.removeAttribute('disabled');
          } else {
            item.setAttribute('disabled', true);
          }
          item.checked = false;
          item.dataset.inCart = 'false';
          if (activeGift && item.value == activeId) {
            item.checked = true;
            item.dataset.inCart = 'true';
          }
        });

        // Validate the gift
        if (validate) {
          if (options.reached_threshold) {
            // Only do something if auto select is enabled
            if (this.autoSelect && this.options.auto_select) {
              if (!activeGift && this.autoSelectTemporary) {
                this.setGiftIsAddedByAutoSelect(true);
                this.update(this.giftItems[0].value, true);
              } else if (this.giftId && discount < previousDiscount) {
                this.setGiftIsAddedByAutoSelect(false);
                this.update(this.giftId, false);
              }
            }
          } else {
            if (activeGift) {
              this.update(activeId, false);
            } else if (this.giftId && this.autoSelectTemporary) {
              this.update(this.giftId, false);
            } else if (this.giftId && this.giftIsAddedByAutoSelect) {
              this.update(this.giftId, false);
            }
            this.setGiftIsAddedByAutoSelect(false);
          }
        }

        // Auto select temporary is disabled because of minus quantity change enable it for the next run
        this.autoSelectTemporary = true;

        // Update the options
        this.options = { ...options };
      } catch (error) {
        console.error(error);
      }
    }
  }

  customElements.define('cart-gift', CartGift);
}
